/**
 * Reminder date-resolution logic, shared by the client popup (display +
 * countdown) and the server (calendar + email target). Single source of
 * truth so the date the visitor sees and the date we actually remind them
 * about can never drift apart.
 *
 * Rule (per plan Decision 6):
 *   - Target the soonest UPCOMING and BOOKABLE session.
 *   - "Bookable" excludes sold-out / cancelled / completed / draft sessions
 *     and sessions already at capacity, so we never point a reminder (or the
 *     reward) at a dead checkout.
 *   - If the soonest bookable session is < 24h away, show a live countdown to
 *     it AND default the reminder to the NEXT bookable session after it
 *     (falling back to the soonest one if there is no next).
 *   - If there are no upcoming bookable sessions, there is no target and the
 *     popup should not fire.
 */

export interface ReminderSession {
  id: number;
  startDatetime: Date | string;
  status: string;
  seatsSold?: number | null;
  maxCapacity?: number | null;
}

export interface ReminderTargetResult<T extends ReminderSession> {
  /** Soonest bookable session, but only when it is < 24h away (else null). */
  countdownTarget: T | null;
  /** The session the reminder + calendar invite should point at (or null). */
  reminderTarget: T | null;
}

const NON_BOOKABLE_STATUSES = new Set([
  "sold_out",
  "cancelled",
  "canceled",
  "completed",
  "draft",
]);

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

function toMs(d: Date | string): number {
  return (typeof d === "string" ? new Date(d) : d).getTime();
}

export function isBookableSession(s: ReminderSession, now: Date): boolean {
  if (toMs(s.startDatetime) <= now.getTime()) return false;
  if (NON_BOOKABLE_STATUSES.has(s.status)) return false;
  if (s.maxCapacity != null && s.seatsSold != null && s.seatsSold >= s.maxCapacity) return false;
  return true;
}

export function resolveReminderTarget<T extends ReminderSession>(
  sessions: T[],
  now: Date = new Date(),
): ReminderTargetResult<T> {
  const bookable = sessions
    .filter((s) => isBookableSession(s, now))
    .sort((a, b) => toMs(a.startDatetime) - toMs(b.startDatetime));

  const soonest = bookable[0] ?? null;

  // Countdown is shown for the imminent (< 24h) session — pure urgency to
  // register now. The reminder itself never points at it (its day-before
  // moment is already here/past, so a reminder would be useless).
  const countdownTarget =
    soonest && toMs(soonest.startDatetime) - now.getTime() < TWENTY_FOUR_HOURS_MS ? soonest : null;

  // The reminder targets the soonest bookable session that is at least ~24h
  // out, so both the day-before email and the calendar event land in the
  // future. (When the soonest session is itself >= 24h out, this is just it.)
  const reminderTarget =
    bookable.find((s) => toMs(s.startDatetime) - now.getTime() >= TWENTY_FOUR_HOURS_MS) ?? null;

  return { countdownTarget, reminderTarget };
}
